import React from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PartyPopper, Megaphone, ShieldAlert, Bus, Heart, Utensils, Home } from "lucide-react";
import Circulars from "@/pages/admin/Circulars";
import { EventsTab } from "@/pages/admin/Services";

const MANAGEMENT_ROLES = ["platform_admin", "tenant_owner", "administrator", "principal"];

export default function Announcements() {
  const { user } = useAuth();
  const { t, language } = useI18n();
  const canManage = MANAGEMENT_ROLES.some((role) => user?.roles?.includes(role));

  const eventsLabel = language === "ar" ? "الفعاليات" : "Events";
  const circularsLabel = language === "ar" ? "التعاميم" : "Circulars";
    const emergencyLabel = language === "ar" ? "إعلانات الطوارئ" : "Emergencies";
  const transportLabel = language === "ar" ? "النقل" : "Transport";
  const healthLabel = language === "ar" ? "الصحة" : "Health";
  const mealsLabel = language === "ar" ? "الوجبات" : "Meals";
  const hostelLabel = language === "ar" ? "السكن" : "Hostel";
  const hideDormantServiceOptions = !!(user?.roles?.includes("administrator") || user?.roles?.includes("principal"));
  const showServiceOptionsInAnnouncementsMenu = !hideDormantServiceOptions;
  const emergencySubtitle = language === "ar"
    ? "إعلانات الطوارئ المرئية لأولياء الأمور حتى التاريخ المحدد"
    : "Emergency announcements visible to parents until the selected date";

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="announcements-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.announcementsTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.announcementsSubtitle")}
        </p>
      </div>

      <Tabs defaultValue={canManage ? "events" : "circulars"}>
        <TabsList className="flex-wrap h-auto">
          {canManage && showServiceOptionsInAnnouncementsMenu && (
            <TabsTrigger value="transport" disabled data-testid="tab-announcement-transport-disabled">
              <Bus className="h-3.5 w-3.5 mr-1.5" /> {transportLabel}
            </TabsTrigger>
          )}
          {canManage && showServiceOptionsInAnnouncementsMenu && (
            <TabsTrigger value="health" disabled data-testid="tab-announcement-health-disabled">
              <Heart className="h-3.5 w-3.5 mr-1.5" /> {healthLabel}
            </TabsTrigger>
          )}
          {canManage && showServiceOptionsInAnnouncementsMenu && (
            <TabsTrigger value="meals" disabled data-testid="tab-announcement-meals-disabled">
              <Utensils className="h-3.5 w-3.5 mr-1.5" /> {mealsLabel}
            </TabsTrigger>
          )}
          {canManage && showServiceOptionsInAnnouncementsMenu && (
            <TabsTrigger value="hostel" disabled data-testid="tab-announcement-hostel-disabled">
              <Home className="h-3.5 w-3.5 mr-1.5" /> {hostelLabel}
            </TabsTrigger>
          )}
          {canManage && (
            <TabsTrigger value="events" data-testid="tab-announcement-events">
              <PartyPopper className="h-3.5 w-3.5 mr-1.5" /> {eventsLabel}
            </TabsTrigger>
          )}
          <TabsTrigger value="circulars" data-testid="tab-announcement-circulars">
            <Megaphone className="h-3.5 w-3.5 mr-1.5" /> {circularsLabel}
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="emergency" data-testid="tab-announcement-emergency">
              <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> {emergencyLabel}
            </TabsTrigger>
          )}
        </TabsList>

        {canManage && (
          <TabsContent value="events">
            <EventsTab embedded />
          </TabsContent>
        )}

        <TabsContent value="circulars">
          <Circulars
            embedded
            forcedKind="circular"
            titleOverride={circularsLabel}
            subtitleOverride={t("staffCirculars.subtitle")}
          />
        </TabsContent>

        {canManage && (
          <TabsContent value="emergency">
            <Circulars
              embedded
              forcedKind="emergency"
              titleOverride={emergencyLabel}
              subtitleOverride={emergencySubtitle}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
